import React, { useEffect, useState } from "react";
import { useAllergenApi } from "../../api/AllergenApi";
import type { AllergenInput } from "../../types/Recipe";
import { handleApiError } from "../../utils/handleApiError";
import Select from "react-select";

interface AllergenSelectorProps {
  allergens: AllergenInput[];
  setAllergens: React.Dispatch<React.SetStateAction<AllergenInput[]>>;
}

const AllergenSelector: React.FC<AllergenSelectorProps> = ({
  allergens,
  setAllergens,
}) => {
  const [availableAllergens, setAvailableAllergens] = useState<
    { id: number; name: string }[]
  >([]);
  const { getAllergens, createAllergen } = useAllergenApi();

  useEffect(() => {
    (async () => {
      try {
        const response = await getAllergens();
        const data = response.data.data;
        setAvailableAllergens(data);
      } catch (err: any) {
        handleApiError(err, "Feil ved lasting av allergener.");
      }
    })();
  }, []);

  const addAllergen = () => {
    setAllergens([...allergens, { id: 0 }]);
  };

  const updateAllergen = (index: number, value: any) => {
    const updated = [...allergens];
    updated[index].id = value;
    setAllergens(updated);
  };

  const removeAllergen = (index: number) =>
    setAllergens(allergens.filter((_, i) => i !== index));

  const handleNewAllergen = async (name: string) => {
    if (!name.trim()) return;

    try {
      const response = await createAllergen(name);
      const created = response.data.data;

      // Add new allergen to the list
      setAvailableAllergens((prev) => [...prev, created]);

      return created;
    } catch (err: any) {
      // If allergen already exists (409),
      // find and return the existing one instead of crashing.
      if (err.status === 409) {
        const existing = availableAllergens.find(
          (a) => a.name.toLowerCase() === name.toLowerCase(),
        );

        if (existing) return existing;
      }

      handleApiError(err, "Feil ved oppretting av ny allergen.");
    }
  };

  // Filter options for a row: include all not selected elsewhere, or currently selected in this row
  const getOptionsForRow = (currentIndex: number) =>
    availableAllergens.filter(
      (all) =>
        !allergens.some((sel, i) => i !== currentIndex && sel.id === all.id),
    );

  return (
    <div className="border-t pt-3 mt-3">
      <h2 className="font-semibold text-lg text-orange-600 mb-2">Allergener</h2>

      {allergens.map((all, index) => (
        <div key={index} className="flex gap-2 mb-2">
          <Select
            value={
              all.id && all.id !== "_new"
                ? {
                    value: all.id,
                    label:
                      availableAllergens.find((opt) => opt.id === all.id)
                        ?.name || "",
                  }
                : all.id === "_new"
                  ? { value: "_new", label: "➕ Lag ny/søk..." }
                  : null
            }
            onChange={(selected) => {
              if (selected?.value === "_new") {
                updateAllergen(index, "_new");
              } else {
                updateAllergen(index, Number(selected?.value));
              }
            }}
            options={[
              { value: "_new", label: "➕ Lag ny..." },
              ...getOptionsForRow(index).map((opt) => ({
                value: opt.id,
                label: opt.name,
              })),
            ]}
            placeholder="Søk etter allergen..."
            isClearable={false}
            className="flex-1"
          />

          {all.id === "_new" && (
            <input
              type="text"
              placeholder="Nytt allergen"
              autoFocus
              onBlur={async (e) => {
                const newOne = await handleNewAllergen(e.target.value);
                if (newOne) updateAllergen(index, newOne.id);
              }}
              className="flex-1 border border-gray-300 rounded-lg px-2 py-1"
            />
          )}

          <button
            type="button"
            onClick={() => removeAllergen(index)}
            className="text-red-500 font-bold"
          >
            ✕
          </button>
        </div>
      ))}

      <button
        type="button"
        onClick={addAllergen}
        className="bg-orange-100 text-orange-700 px-3 py-1 rounded-lg hover:bg-orange-200"
      >
        ➕ Legg til allergen
      </button>
    </div>
  );
};

export default AllergenSelector;
